import { useState, useCallback } from "react";
import { ExternalLink, Loader2, X, Globe, Smartphone } from "lucide-react";
import { toast } from "sonner";

/**
 * In-App Browser overlay — keeps user inside BirthdayDeaLight.
 */
const InAppBrowser = ({ url, businessName, onClose }) => (
  <div className="fixed inset-0 z-[9999] bg-black/80 flex flex-col" data-testid="in-app-browser">
    <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-white/10 shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <Globe className="w-4 h-4 text-white/50 shrink-0" />
        <span className="text-xs text-white/70 truncate">{businessName}</span>
      </div>
      <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition" data-testid="browser-close-btn">
        <X className="w-5 h-5 text-white/70" />
      </button>
    </div>
    <iframe
      src={url}
      className="flex-1 w-full bg-white"
      title={businessName}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      data-testid="browser-iframe"
    />
  </div>
);

/**
 * ExternalLinkButtons — renders Open App and/or Visit Website buttons.
 *
 * Props:
 *   app_store_url  — nullable string
 *   website_url    — nullable string
 *   business_name  — string for in-app browser header
 */
export default function ExternalLinkButtons({ app_store_url, website_url, business_name }) {
  const [loading, setLoading] = useState(null); // "app" | "website" | null
  const [browserUrl, setBrowserUrl] = useState(null);

  const hasApp = !!app_store_url;
  const hasWeb = !!website_url;

  const handleOpenApp = useCallback(() => {
    if (!app_store_url) return;
    setLoading("app");

    // Attempt to open the app store URL (deep link intent on Android)
    const opened = window.open(app_store_url, "_blank");
    const timer = setTimeout(() => {
      // After 500ms timeout — if the page is still visible, the app didn't open
      // Fall back to in-app browser
      if (document.visibilityState !== "hidden") {
        if (website_url) {
          toast("Opening website instead", { duration: 2000 });
          setBrowserUrl(website_url);
        } else {
          setBrowserUrl(app_store_url);
        }
      }
      setLoading(null);
    }, 500);

    // If window.open returned null (blocked), use fallback immediately
    if (!opened) {
      clearTimeout(timer);
      setBrowserUrl(website_url || app_store_url);
      setLoading(null);
    }
  }, [app_store_url, website_url]);

  const handleOpenWebsite = useCallback(() => {
    if (!website_url) return;
    setLoading("website");
    // Small delay for spinner visibility
    setTimeout(() => {
      setBrowserUrl(website_url);
      setLoading(null);
    }, 300);
  }, [website_url]);

  // Render nothing when both are null
  if (!hasApp && !hasWeb) return null;

  const bothExist = hasApp && hasWeb;

  return (
    <>
      <div className={`flex gap-2 ${bothExist ? "" : ""}`} data-testid="external-link-buttons">
        {hasApp && (
          <button
            onClick={handleOpenApp}
            disabled={loading === "app"}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-xs transition min-h-[44px] bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700 disabled:opacity-60 ${bothExist ? "flex-1" : "w-full"}`}
            data-testid="open-app-btn"
          >
            {loading === "app" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
            Open App
          </button>
        )}
        {hasWeb && (
          <button
            onClick={handleOpenWebsite}
            disabled={loading === "website"}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-xs transition min-h-[44px] border-2 border-slate-200 text-slate-700 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-60 ${bothExist ? "flex-1" : "w-full"}`}
            data-testid="visit-website-btn"
          >
            {loading === "website" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            Visit Website
          </button>
        )}
      </div>

      {/* In-App Browser Overlay */}
      {browserUrl && (
        <InAppBrowser
          url={browserUrl}
          businessName={business_name || "Website"}
          onClose={() => setBrowserUrl(null)}
        />
      )}
    </>
  );
}
